/**
 * Pushes a vehicle's plate onto the Hikvision LPR cameras of the zones its owner is authorized
 * for — the LPR counterpart to syncing an RFID tag to a zone's readers.
 *
 * Zone → camera resolution lives in utils/vehicleRfidSync (fetchPersonVehicleDeviceIds returns
 * lprCameraIds); the ISAPI protocol lives in hikvisionLprPlates. This module is only the
 * orchestration between them, which is why its dependencies are injectable: the decision logic
 * (which cameras, partial failure, revoke) is worth testing without Electron or a network.
 *
 * Transport is Electron's deviceHttpRequest, so plates are written over the LAN by the desktop
 * app rather than from the cloud.
 */

import { listHikvisionLPRCameras, type HikvisionLPRCamera } from './hikvisionLprCameraService';
import { fetchWithAuth } from './api';
import {
  addPlate as addPlateToCamera,
  removePlate as removePlateFromCamera,
  listPlates as listPlatesFromCamera,
  normalizePlate,
  type LprCameraTarget,
  type LprHttpRequest,
  type LprPlateInput,
} from './hikvisionLprPlates';

export interface LprSyncFailure {
  cameraId: string;
  name: string;
  message: string;
}

export interface LprSyncResult {
  ok: string[];
  failed: LprSyncFailure[];
}

export interface LprSyncVehicle {
  plate: string;
  /** Shown as "Belong to" on the camera, so a guard reading its list sees who the plate is. */
  ownerName?: string;
}

export interface LprSyncDeps {
  listCameras: (customerId: string) => Promise<HikvisionLPRCamera[]>;
  /** Reads the camera's current allow-list. */
  listPlates?: (target: LprCameraTarget, request: LprHttpRequest) => Promise<Array<{ plate: string; listType: string }>>;
  /** Vehicles belonging to a person, used when revoking every plate they own. */
  listPersonVehicles?: (customerId: string, personId: string) => Promise<Array<{ plate?: string | null }>>;
  addPlate: (target: LprCameraTarget, request: LprHttpRequest, entry: LprPlateInput) => Promise<void>;
  removePlate: (target: LprCameraTarget, request: LprHttpRequest, plate: string) => Promise<void>;
  request: LprHttpRequest;
}

/**
 * Electron's LAN HTTP channel, which handles the digest auth these cameras require.
 * Throws outside Electron rather than silently doing nothing — plates are synced over the LAN
 * by the desktop app, so a browser-only session genuinely cannot perform this.
 */
const electronRequest: LprHttpRequest = async (config) => {
  const api = (window as any)?.electronAPI;
  if (!api?.deviceHttpRequest) {
    throw new Error(
      'Sincronização de placas exige o aplicativo desktop (a câmera é acessada pela rede local)',
    );
  }
  return api.deviceHttpRequest(config);
};

async function listPersonVehiclesDefault(
  customerId: string,
  personId: string,
): Promise<Array<{ plate?: string | null }>> {
  const res = await fetchWithAuth(
    `/api/v1/customers/${customerId}/vehicles?person_id=${personId}&page_size=500`,
  );
  if (!res.ok) {
    throw new Error('Não foi possível carregar os veículos da pessoa');
  }
  const data = await res.json();
  return data?.data ?? data?.vehicles ?? [];
}

const defaultDeps: LprSyncDeps = {
  listCameras: listHikvisionLPRCameras,
  listPlates: listPlatesFromCamera as LprSyncDeps['listPlates'],
  listPersonVehicles: listPersonVehiclesDefault,
  addPlate: addPlateToCamera,
  removePlate: removePlateFromCamera,
  request: electronRequest,
};

function toTarget(camera: HikvisionLPRCamera): LprCameraTarget {
  return {
    baseUrl: `http://${camera.device_ip}:${camera.device_port || 80}`,
    username: camera.username,
    password: camera.password,
    channelId: camera.channel_id || 1,
  };
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * Adds or removes `vehicle.plate` on the given cameras.
 *
 * Never throws: a camera that fails is reported in `failed` and the rest still run, so one
 * unreachable gate cannot stop the others. Callers must surface `failed` — reporting a partial
 * sync as success is the failure mode this shape exists to prevent.
 */
export async function syncPlateToLprCameras(
  customerId: string,
  cameraIds: string[],
  vehicle: LprSyncVehicle,
  isAllowed: boolean,
  deps: LprSyncDeps = defaultDeps,
): Promise<LprSyncResult> {
  const ok: string[] = [];
  const failed: LprSyncFailure[] = [];

  if (!cameraIds || cameraIds.length === 0) {
    return { ok, failed };
  }

  const plate = normalizePlate(vehicle.plate);
  if (!plate) {
    return {
      ok,
      failed: cameraIds.map(cameraId => ({
        cameraId,
        name: '',
        message: `Placa inválida para sincronizar na câmera LPR: "${vehicle.plate}"`,
      })),
    };
  }

  let cameras: HikvisionLPRCamera[];
  try {
    cameras = await deps.listCameras(customerId);
  } catch (err) {
    // Fail closed and loud — see the test "reports every camera as failed".
    return {
      ok,
      failed: cameraIds.map(cameraId => ({
        cameraId,
        name: '',
        message: errorMessage(err, 'Não foi possível carregar as câmeras LPR'),
      })),
    };
  }

  const byId = new Map(cameras.map(c => [c.id, c]));

  for (const cameraId of cameraIds) {
    const camera = byId.get(cameraId);

    // A camera that is gone or switched off is not a failure — it is simply not a target.
    if (!camera || !camera.is_active) continue;

    if (!camera.device_ip) {
      failed.push({ cameraId, name: camera.name, message: `Câmera ${camera.name} sem IP configurado` });
      continue;
    }

    try {
      const target = toTarget(camera);
      if (isAllowed) {
        await deps.addPlate(target, deps.request, { plate, name: vehicle.ownerName ?? '' });
      } else {
        await deps.removePlate(target, deps.request, plate);
      }
      ok.push(cameraId);
    } catch (err) {
      failed.push({
        cameraId,
        name: camera.name,
        message: errorMessage(err, isAllowed ? 'Erro ao sincronizar a placa' : 'Erro ao remover a placa'),
      });
    }
  }

  return { ok, failed };
}

/** One-line operator summary, or null when nothing failed. */
export function describeLprSyncFailures(result: LprSyncResult): string | null {
  if (result.failed.length === 0) return null;
  const parts = result.failed.map(f => `${f.name || f.cameraId}: ${f.message}`);
  const noun = result.failed.length > 1 ? 'Câmeras LPR não sincronizadas' : 'Câmera LPR não sincronizada';
  return `${noun} — ${parts.join('; ')}`;
}

export interface LprRevokeOutcome {
  allSucceeded: boolean;
  removed: string[];
  errorMessage?: string;
}

/**
 * Removes every plate belonging to a person from one LPR camera.
 *
 * This is what "remove this person from this device" means for a DS-TCG406: the camera has no
 * user database, so the face/card deletion the deferred-removal queue performs for other
 * devices does not apply to it and would fail on every retry. Without this, a revoked visitor
 * keeps opening the gate by plate while being removed from every other device.
 *
 * Returns rather than throws so the queue can report the real outcome; `allSucceeded: false`
 * keeps the item queued for retry, which is the correct behaviour for a partial removal.
 */
export async function revokePersonPlatesFromCamera(
  customerId: string,
  personId: string,
  cameraId: string,
  deps: LprSyncDeps = defaultDeps,
): Promise<LprRevokeOutcome> {
  const listVehicles = deps.listPersonVehicles ?? listPersonVehiclesDefault;

  let vehicles: Array<{ plate?: string | null }>;
  try {
    vehicles = await listVehicles(customerId, personId);
  } catch (err) {
    // Unknown vehicle set — must not be reported as "nothing to remove".
    return { allSucceeded: false, removed: [], errorMessage: errorMessage(err, 'Falha ao carregar veículos') };
  }

  const plates = vehicles
    .map(v => normalizePlate(v.plate ?? ''))
    .filter((p): p is string => p !== null);

  if (plates.length === 0) {
    return { allSucceeded: true, removed: [] };
  }

  let cameras: HikvisionLPRCamera[];
  try {
    cameras = await deps.listCameras(customerId);
  } catch (err) {
    return { allSucceeded: false, removed: [], errorMessage: errorMessage(err, 'Falha ao carregar câmeras LPR') };
  }

  const camera = cameras.find(c => c.id === cameraId);
  if (!camera || !camera.device_ip) {
    return {
      allSucceeded: false,
      removed: [],
      errorMessage: `Câmera LPR ${cameraId} não encontrada ou sem IP`,
    };
  }

  const target = toTarget(camera);
  const removed: string[] = [];
  const failures: string[] = [];

  for (const plate of plates) {
    try {
      await deps.removePlate(target, deps.request, plate);
      removed.push(plate);
    } catch (err) {
      failures.push(`${plate}: ${errorMessage(err, 'falha ao remover')}`);
    }
  }

  return failures.length === 0
    ? { allSucceeded: true, removed }
    : { allSucceeded: false, removed, errorMessage: failures.join('; ') };
}

/**
 * The plates a camera is physically holding, normalized and allow-list only.
 *
 * Backs the "Veículos no Dispositivo (cadastrados fisicamente)" list in the device modal: for
 * an RFID reader that comes from the reader's card dump, for an LPR camera it is this read.
 *
 * Returns [] rather than throwing when the camera is unknown or unreachable — an offline gate
 * must not break the modal; it simply shows nothing registered on the device.
 */
export async function listPlatesOnCamera(
  customerId: string,
  cameraId: string,
  deps: LprSyncDeps = defaultDeps,
): Promise<string[]> {
  try {
    const cameras = await deps.listCameras(customerId);
    const camera = cameras.find(c => c.id === cameraId);
    if (!camera || !camera.device_ip) return [];

    const read = deps.listPlates ?? (listPlatesFromCamera as NonNullable<LprSyncDeps['listPlates']>);
    const entries = await read(toTarget(camera), deps.request);

    return entries
      .filter(e => e.listType === 'allowList')
      .map(e => normalizePlate(e.plate))
      .filter((p): p is string => p !== null);
  } catch {
    return [];
  }
}

export interface LprCameraSyncProgress {
  done: number; total: number; ok: number; fail: number; label: string; status: 'ok' | 'failed';
}

export interface LprCameraSyncResult {
  total: number; ok: number; fail: number;
  failures: Array<{ plate: string; message: string }>;
}

/**
 * Pushes a set of vehicles' plates onto one camera — what "Sincronizar" means for an LPR device.
 *
 * Previously the device and zone sync actions ran the people-sync path for LPR cameras and
 * reported "Nenhuma pessoa autorizada": a camera holds no people, only plates.
 *
 * One plate per call, since that is the only shape verified against the hardware. Never throws;
 * a camera that cannot be resolved fails every plate rather than silently reporting success.
 */
export async function syncAllPlatesToCamera(
  customerId: string,
  cameraId: string,
  vehicles: Array<{ plate?: string | null; owner_name?: string | null }>,
  deps: LprSyncDeps = defaultDeps,
  opts: { onProgress?: (p: LprCameraSyncProgress) => void } = {},
): Promise<LprCameraSyncResult> {
  const plated = (vehicles || [])
    .map(v => ({ plate: normalizePlate(v.plate ?? ''), owner: v.owner_name ?? '' }))
    .filter((v): v is { plate: string; owner: string } => v.plate !== null);

  const result: LprCameraSyncResult = { total: plated.length, ok: 0, fail: 0, failures: [] };
  if (plated.length === 0) return result;

  let camera: HikvisionLPRCamera | undefined;
  try {
    camera = (await deps.listCameras(customerId)).find(c => c.id === cameraId);
  } catch {
    camera = undefined;
  }

  if (!camera || !camera.device_ip) {
    result.fail = plated.length;
    result.failures = plated.map(v => ({ plate: v.plate, message: 'Câmera LPR não encontrada ou sem IP' }));
    return result;
  }

  const target = toTarget(camera);
  let done = 0;

  for (const v of plated) {
    let okThis = true;
    try {
      await deps.addPlate(target, deps.request, { plate: v.plate, name: v.owner });
      result.ok++;
    } catch (err) {
      okThis = false;
      result.fail++;
      result.failures.push({ plate: v.plate, message: errorMessage(err, 'falha ao gravar a placa') });
    }
    done++;
    opts.onProgress?.({
      done, total: plated.length, ok: result.ok, fail: result.fail,
      label: v.plate, status: okThis ? 'ok' : 'failed',
    });
  }

  return result;
}
